import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AddressFields,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { Decimal, merchantEarning, type Address } from '@mlm/shared';
import { apiErrorMessage, getTenantFeatures, type TenantFeatures } from '../lib/api';
import {
  approveMerchant,
  approveMerchantProduct,
  createMerchant,
  createMerchantPayoutBatch,
  exportMerchantPayoutBatch,
  getMerchant,
  listMerchantPayoutBatches,
  listMerchantPayouts,
  listMerchantProducts,
  listMerchants,
  markMerchantPayoutBatchPaid,
  markMerchantPayoutPaid,
  rejectMerchant,
  rejectMerchantPayout,
  rejectMerchantPayoutBatch,
  rejectMerchantProduct,
  requirementFileUrl,
  reviewRequirement,
  suspendMerchant,
  updateMerchantCommission,
  type AdminMerchantDetail,
  type AdminMerchantProductRow,
  type ApproveMerchantProductArgs,
  type ApproveMerchantProductVariantOverride,
} from '../lib/merchants-api';

const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  approved: 'default',
  pending: 'secondary',
  requested: 'secondary',
  rejected: 'destructive',
  suspended: 'destructive',
  paid: 'default',
  cancelled: 'outline',
};

export function MerchantsPage() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Merchants</h1>
      <Tabs defaultValue="accounts">
        <TabsList>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
        </TabsList>
        <TabsContent value="accounts">
          <AccountsTab />
        </TabsContent>
        <TabsContent value="products">
          <ProductsTab />
        </TabsContent>
        <TabsContent value="payouts">
          <PayoutsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Accounts ────────────────────────────────────────────────────────────
function AccountsTab() {
  const [status, setStatus] = useState('pending');
  const [reviewing, setReviewing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { data } = useQuery({
    queryKey: ['admin-merchants', status],
    queryFn: () => listMerchants({ status }),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <StatusFilter
          value={status}
          onChange={setStatus}
          options={['pending', 'approved', 'rejected', 'suspended']}
        />
        <Button size="sm" onClick={() => setCreating(true)}>
          Create merchant
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Business</th>
                <th className="p-3">Owner</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((m) => (
                <tr key={m.userId} className="border-b last:border-0">
                  <td className="p-3 font-medium">{m.businessName}</td>
                  <td className="p-3">
                    {m.ownerName || m.ownerEmail}
                    <div className="text-xs text-muted-foreground">{m.ownerEmail}</div>
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[m.status]} className="capitalize">
                      {m.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setReviewing(m.userId)}>
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    None.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {reviewing && <MerchantReviewDialog userId={reviewing} onClose={() => setReviewing(null)} />}
      {creating && (
        <CreateMerchantDialog
          onClose={() => setCreating(false)}
          onCreated={(userId) => {
            // New merchants land PENDING; show the pending queue and open the
            // detail so the admin can review documents and approve.
            setStatus('pending');
            setCreating(false);
            setReviewing(userId);
          }}
        />
      )}
    </div>
  );
}

function CreateMerchantDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (userId: string) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    businessName: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    contactNumber: '',
    contactEmail: '',
    pickupRegionCode: '',
    platformFeePct: '',
  });
  const [showFeeToMerchant, setShowFeeToMerchant] = useState(false);
  const [delivery, setDelivery] = useState<Address>({});
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const deliveryComplete =
    !!delivery.line1?.trim() &&
    !!delivery.regionCode &&
    !!delivery.provinceCode &&
    !!delivery.cityCode &&
    !!delivery.barangayCode &&
    !!delivery.postalCode?.trim() &&
    !!delivery.contactNo?.trim() &&
    !!delivery.contactPerson?.trim() &&
    !!delivery.fullAddress?.trim();

  const create = useMutation({
    mutationFn: async () => {
      return createMerchant({
        businessName: form.businessName.trim(),
        email: form.email.trim(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        contactNumber: form.contactNumber.trim(),
        contactEmail: form.contactEmail.trim(),
        pickupRegionCode: form.pickupRegionCode.trim() || undefined,
        pickupAddress: delivery,
        platformFeePct: form.platformFeePct.trim(),
        showFeeToMerchant,
      });
    },
    meta: { successMessage: 'Merchant created.' },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['admin-merchants'] });
      onCreated(res.userId);
    },
  });

  // Commission % is required (0 allowed, blank not).
  const feeValid =
    form.platformFeePct.trim() !== '' &&
    Number.isFinite(Number(form.platformFeePct)) &&
    Number(form.platformFeePct) >= 0;

  const canSubmit =
    !!form.businessName.trim() &&
    !!form.email.trim() &&
    form.password.length >= 8 &&
    !!form.firstName.trim() &&
    !!form.lastName.trim() &&
    !!form.phone.trim() &&
    !!form.contactNumber.trim() &&
    !!form.contactEmail.trim() &&
    feeValid &&
    deliveryComplete;

  const errorCode = (create.error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error;
  const errorMsg =
    errorCode === 'email_taken'
      ? 'That email already has an account.'
      : create.isError
        ? 'Could not create merchant. Check the fields and try again.'
        : null;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create merchant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Creates a <span className="font-medium">pending</span> merchant and its login. The
            merchant is approved in a separate step after you've reviewed their requirements. Share
            the password with them.
          </p>
          <Field label="Business name">
            <Input
              value={form.businessName}
              onChange={set('businessName')}
              placeholder="Acme Trading"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Login email">
              <Input
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="owner@acme.com"
              />
            </Field>
            <Field label="Temporary password">
              <Input
                type="text"
                value={form.password}
                onChange={set('password')}
                placeholder="min 8 characters"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Owner first name">
              <Input value={form.firstName} onChange={set('firstName')} />
            </Field>
            <Field label="Owner last name">
              <Input value={form.lastName} onChange={set('lastName')} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Contact # *">
              <Input value={form.phone} onChange={set('phone')} />
            </Field>
            <Field label="Shipping zone code (optional)">
              <Input
                value={form.pickupRegionCode}
                onChange={set('pickupRegionCode')}
                placeholder="e.g. metro_manila"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Business contact # *">
              <Input value={form.contactNumber} onChange={set('contactNumber')} />
            </Field>
            <Field label="Business contact email *">
              <Input type="email" value={form.contactEmail} onChange={set('contactEmail')} />
            </Field>
          </div>
          <div className="rounded-md border p-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              FF delivery details (required)
            </div>
            <AddressFields
              value={delivery}
              onChange={setDelivery}
              requireContactNo
              showDeliveryExtras
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Commission % for this merchant *">
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={form.platformFeePct}
                onChange={set('platformFeePct')}
                placeholder="e.g. 10 (0 allowed)"
              />
            </Field>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={showFeeToMerchant}
                onChange={(e) => setShowFeeToMerchant(e.target.checked)}
              />
              Show earnings to merchant
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Required — the platform commission charged on this merchant's sales (0 is allowed;
            clamped to the allowed range). PV is derived from it on the admin side at product
            approval. When “Show earnings” is off, the merchant's product form hides the earnings
            preview.
          </p>
          {errorMsg && <p className="text-destructive">{errorMsg}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create merchant'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function MerchantReviewDialog({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin-merchant', userId],
    queryFn: () => getMerchant(userId),
  });
  const [reason, setReason] = useState('');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-merchants'] });
    qc.invalidateQueries({ queryKey: ['admin-merchant', userId] });
  };
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      invalidate();
    },
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data?.merchant.businessName ?? 'Merchant'}</DialogTitle>
        </DialogHeader>
        {data && (
          <MerchantReviewBody
            data={data}
            reason={reason}
            setReason={setReason}
            act={act}
            userId={userId}
            onClose={onClose}
            invalidate={invalidate}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MerchantReviewBody({
  data,
  reason,
  setReason,
  act,
  userId,
  onClose,
  invalidate,
}: {
  data: AdminMerchantDetail;
  reason: string;
  setReason: (s: string) => void;
  act: ReturnType<typeof useMutation<unknown, unknown, () => Promise<unknown>>>;
  userId: string;
  onClose: () => void;
  invalidate: () => void;
}) {
  const m = data.merchant;
  const isApproved = m.status === 'approved';
  // Commission is required to approve / save changes (0 allowed, blank not).
  const [fee, setFee] = useState(m.platformFeePct != null ? String(Number(m.platformFeePct)) : '');
  const [show, setShow] = useState(m.showFeeToMerchant);
  const feeValid = fee.trim() !== '' && Number.isFinite(Number(fee)) && Number(fee) >= 0;

  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-1">
        <div>
          <span className="text-muted-foreground">Owner:</span> {m.ownerName || m.ownerEmail} (
          {m.ownerEmail})
        </div>
        <div>
          <span className="text-muted-foreground">Status:</span>{' '}
          <Badge variant={statusVariant[m.status]} className="capitalize">
            {m.status}
          </Badge>
        </div>
        <div>
          <span className="text-muted-foreground">Pickup region:</span> {m.pickupRegionCode || '—'}
        </div>
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <div className="text-xs font-medium text-muted-foreground">Commission</div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Commission % (required, 0 allowed)">
            <Input
              type="number"
              step="0.01"
              min={0}
              max={100}
              className="w-40"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="e.g. 10"
            />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            Show earnings to merchant
          </label>
        </div>
        <p className="text-xs text-muted-foreground">
          {isApproved
            ? 'Saved with “Save changes” below. Applies to products created after the change (existing products keep their snapshot).'
            : 'Saved when you Approve. Applies to products created after approval.'}
        </p>
      </div>

      <div>
        <div className="font-medium mb-1">Requirements</div>
        {data.submissions.length === 0 ? (
          <p className="text-muted-foreground">No submissions.</p>
        ) : (
          <ul className="space-y-2">
            {data.submissions.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 border-b pb-2">
                <div>
                  <div className="font-medium">{s.requirementKey}</div>
                  {s.textValue && (
                    <div className="text-xs text-muted-foreground">{s.textValue}</div>
                  )}
                  {s.fileKey && <FileLink submissionId={s.id} />}
                  <Badge variant={statusVariant[s.status]} className="capitalize mt-1">
                    {s.status}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reviewRequirement(s.id, 'approved').then(invalidate)}
                  >
                    OK
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reviewRequirement(s.id, 'rejected').then(invalidate)}
                  >
                    No
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!isApproved && (
        <Input
          placeholder="Reason (for reject)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}
      {isApproved && (
        <Input
          placeholder="Reason (for suspend)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        {isApproved ? (
          <>
            <Button
              disabled={act.isPending || !feeValid}
              onClick={() =>
                act.mutate(() =>
                  updateMerchantCommission(userId, {
                    platformFeePct: fee.trim(),
                    showFeeToMerchant: show,
                  }).then(invalidate),
                )
              }
            >
              Save changes
            </Button>
            <Button
              variant="destructive"
              disabled={act.isPending}
              onClick={() =>
                act.mutate(() => suspendMerchant(userId, reason).then(() => onClose()))
              }
            >
              Suspend
            </Button>
          </>
        ) : (
          <>
            <Button
              disabled={act.isPending || !feeValid}
              title={!feeValid ? 'Enter the commission % first (0 is allowed)' : undefined}
              onClick={() =>
                act.mutate(() =>
                  approveMerchant(userId, {
                    platformFeePct: fee.trim(),
                    showFeeToMerchant: show,
                  }).then(() => onClose()),
                )
              }
            >
              Approve
            </Button>
            {m.status === 'pending' && (
              <Button
                variant="outline"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate(() => rejectMerchant(userId, reason).then(() => onClose()))
                }
              >
                Reject
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FileLink({ submissionId }: { submissionId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  if (url)
    return (
      <a href={url} target="_blank" rel="noreferrer" className="text-xs underline text-primary">
        Open file
      </a>
    );
  return (
    <button
      className="text-xs underline"
      onClick={() => requirementFileUrl(submissionId).then((r) => setUrl(r.url))}
    >
      View file
    </button>
  );
}

// ── Products ────────────────────────────────────────────────────────────
function ProductsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [reviewing, setReviewing] = useState<AdminMerchantProductRow | null>(null);
  const { data } = useQuery({
    queryKey: ['admin-merchant-products', status],
    queryFn: () => listMerchantProducts({ status }),
  });
  const { data: features } = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-merchant-products'] });

  return (
    <div className="space-y-3">
      <StatusFilter
        value={status}
        onChange={setStatus}
        options={['pending', 'approved', 'rejected']}
      />
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Product</th>
                <th className="p-3">Merchant</th>
                <th className="p-3">Price</th>
                <th className="p-3">Fee %</th>
                <th className="p-3">PV %</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="p-3 font-medium">
                    {p.name}
                    <div className="text-xs text-muted-foreground">{p.sku}</div>
                  </td>
                  <td className="p-3">{p.businessName}</td>
                  <td className="p-3">₱{(Number(p.price) || 0).toFixed(2)}</td>
                  <td className="p-3">
                    {p.platformFeePct != null ? `${Number(p.platformFeePct)}%` : '—'}
                  </td>
                  <td className="p-3">
                    {p.pvCommissionPct != null ? `${Number(p.pvCommissionPct)}%` : '—'}
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[p.approvalStatus]} className="capitalize">
                      {p.approvalStatus}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setReviewing(p)}>
                      Review / Edit
                    </Button>
                  </td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    None.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {reviewing && features && (
        <AdminMerchantProductDialog
          product={reviewing}
          features={features}
          onClose={() => setReviewing(null)}
          onDone={() => {
            invalidate();
            setReviewing(null);
          }}
        />
      )}
    </div>
  );
}

/** Per-variant override inputs for the approval matrix (blank = inherit product). */
type VariantOverrideDraft = { fee: string; pv: string; vatOn: boolean; vatPct: string };

const tabBody = 'h-[30rem] overflow-y-auto pr-1';

/**
 * Per-product review/approval dialog, mirroring the merchant-facing tabbed
 * product editor. Tabs: Overview (read-only), Pricing & PV, Variations
 * (per-variant override matrix), and Realtime Unilevel (only when the plan is on).
 */
function AdminMerchantProductDialog({
  product,
  features,
  onClose,
  onDone,
}: {
  product: AdminMerchantProductRow;
  features: TenantFeatures;
  onClose: () => void;
  onDone: () => void;
}) {
  const price = Number(product.price) || 0;
  const hasVariants = product.variants.length > 0;
  const rtuEnabled = !features.disabled_plans?.includes('realtime_unilevel');

  const [tab, setTab] = useState('overview');
  // Pre-fill the fee/PV from the product's snapshot, else the tenant default —
  // never "NaN" (a missing/blank tenant default would otherwise stringify to it).
  const numStr = (primary: string | null | undefined, fallback: string | null | undefined) => {
    const n = primary != null ? Number(primary) : Number(fallback);
    return Number.isFinite(n) ? String(n) : '0';
  };
  const [fee, setFee] = useState(numStr(product.platformFeePct, features.platform_fee_pct));
  const [pvPct, setPvPct] = useState(
    numStr(product.pvCommissionPct, features.merchant_pv_commission_pct),
  );
  const [vatOn, setVatOn] = useState(!!product.vatEnabled);
  const [vatPct, setVatPct] = useState(
    product.vatPct != null ? String(Number(product.vatPct)) : '12',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feeN = Number(fee) || 0;
  const pvN = Number(pvPct) || 0;
  const commission = (price * feeN) / 100;
  const previewPv = commission * (pvN / 100);

  // Per-variant override drafts, seeded from any existing override (blank = inherit).
  const [vDraft, setVDraft] = useState<Record<string, VariantOverrideDraft>>(() =>
    Object.fromEntries(
      product.variants.map((v) => [
        v.id,
        {
          fee: v.platformFeePct != null ? String(Number(v.platformFeePct)) : '',
          pv: v.pvCommissionPct != null ? String(Number(v.pvCommissionPct)) : '',
          vatOn: v.vatEnabled ?? false,
          vatPct: v.vatPct != null ? String(Number(v.vatPct)) : '',
        },
      ]),
    ),
  );
  const setV = (id: string, patch: Partial<VariantOverrideDraft>) =>
    setVDraft((d) => ({ ...d, [id]: { ...d[id]!, ...patch } }));

  // ── Realtime Unilevel state ──
  // Per-level shares are explicit (% of pool), seeded from the tenant default
  // `merchant_rtu_default_shares`; the admin can tweak/add/remove per product.
  const defaultShares = features.merchant_rtu_default_shares ?? [];
  const [rtuOn, setRtuOn] = useState(product.rtuCeilingPct != null);
  const [ceiling, setCeiling] = useState(
    product.rtuCeilingPct != null
      ? String(Number(product.rtuCeilingPct))
      : String(Number(features.merchant_rtu_ceiling_pct)),
  );
  const [shares, setShares] = useState<string[]>(() =>
    product.rtuLevelShares && product.rtuLevelShares.length > 0
      ? product.rtuLevelShares.map((s) => String(Number(s)))
      : defaultShares.map((s) => String(Number(s))),
  );
  const resetShares = () => setShares(defaultShares.map((s) => String(Number(s))));
  const setShareAt = (idx: number, value: string) =>
    setShares((s) => s.map((v, i) => (i === idx ? value : v)));
  const addLevel = () => setShares((s) => [...s, '0']);
  const removeLevel = (idx: number) =>
    setShares((s) => (s.length > 1 ? s.filter((_, i) => i !== idx) : s));
  const poolPreview = commission * ((Number(ceiling) || 0) / 100);

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      // Round every money string to ≤4 decimals so it satisfies the server's
      // moneyStringSchema (a typed/generated value can otherwise 400).
      const to4 = (s: string) => {
        const t = s.trim();
        if (t === '') return '';
        const n = Number(t);
        return Number.isFinite(n) ? String(Math.round(n * 1e4) / 1e4) : t;
      };
      // The Fee%/PV cells are pre-filled with the inherited product-level rate, so
      // a value equal to that rate (or blank) still means "inherit" — only persist
      // an explicit override when the cell DIFFERS from the current inherited rate.
      // Otherwise variants would silently freeze to a snapshot that no longer
      // follows later product-level changes.
      const variantOverrides = hasVariants
        ? product.variants
            .map((v) => {
              const o = vDraft[v.id]!;
              const feeOverride = o.fee.trim() !== '' && (Number(o.fee) || 0) !== feeN;
              const pvOverride = o.pv.trim() !== '' && (Number(o.pv) || 0) !== pvN;
              const touched = feeOverride || pvOverride || o.vatOn || o.vatPct.trim() !== '';
              if (!touched) return null;
              return {
                variantId: v.id,
                platformFeePct: feeOverride ? to4(o.fee) : null,
                pvCommissionPct: pvOverride ? to4(o.pv) : null,
                vatEnabled: o.vatOn,
                vatPct: o.vatPct.trim() === '' ? null : to4(o.vatPct),
              } as ApproveMerchantProductVariantOverride;
            })
            .filter((x): x is ApproveMerchantProductVariantOverride => x !== null)
        : undefined;
      const args: ApproveMerchantProductArgs = {
        platformFeePct: to4(fee) || undefined,
        pvCommissionPct: to4(pvPct) || undefined,
        vatEnabled: vatOn,
        vatPct: to4(vatPct) || undefined,
        variantOverrides:
          variantOverrides && variantOverrides.length > 0 ? variantOverrides : undefined,
      };
      if (rtuEnabled) {
        if (rtuOn) {
          args.rtuCeilingPct = to4(ceiling) || '0';
          args.rtuLevelShares = shares.map((s) => to4(s) || '0');
          args.rtuLevels = args.rtuLevelShares.length;
        } else {
          args.rtuCeilingPct = null;
          args.rtuLevels = null;
          args.rtuLevelShares = null;
        }
      }
      await approveMerchantProduct(product.id, args);
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to approve'));
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    const reason = window.prompt('Reason for rejection? (optional)') ?? undefined;
    setBusy(true);
    try {
      await rejectMerchantProduct(product.id, reason);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review {product.name}</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pricing">Pricing &amp; PV</TabsTrigger>
            {hasVariants && <TabsTrigger value="variations">Variations</TabsTrigger>}
            {rtuEnabled && <TabsTrigger value="rtu">Realtime Unilevel</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className={tabBody}>
            <dl className="grid grid-cols-[8rem,1fr] gap-2 text-sm">
              <dt className="text-muted-foreground">Product</dt>
              <dd className="font-medium">{product.name}</dd>
              <dt className="text-muted-foreground">SKU</dt>
              <dd className="font-mono text-xs">{product.sku}</dd>
              <dt className="text-muted-foreground">Merchant</dt>
              <dd>{product.businessName}</dd>
              <dt className="text-muted-foreground">Price</dt>
              <dd>₱{price.toFixed(2)}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <Badge variant={statusVariant[product.approvalStatus]} className="capitalize">
                  {product.approvalStatus}
                </Badge>
              </dd>
            </dl>
            {hasVariants && (
              <div className="mt-4">
                <div className="mb-1 text-sm font-medium">Variations</div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {product.variants.map((v) => (
                    <li key={v.id}>
                      {v.name} — ₱{(Number(v.price) || 0).toFixed(2)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="pricing" className={`${tabBody} space-y-4`}>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1 text-sm">
                <span className="font-medium">Platform fee %</span>
                <Input value={fee} onChange={(e) => setFee(e.target.value)} />
              </label>
              <label className="space-y-1 text-sm">
                <span className="font-medium">PV %</span>
                <Input value={pvPct} onChange={(e) => setPvPct(e.target.value)} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={vatOn} onChange={(e) => setVatOn(e.target.checked)} />
              <span className="font-medium">VAT</span>
              <Input
                className="w-20 h-8"
                value={vatPct}
                onChange={(e) => setVatPct(e.target.value)}
                disabled={!vatOn}
              />
              <span>%</span>
            </label>
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
              Commission: <b>₱{commission.toFixed(2)}</b> = ₱{price.toFixed(2)} × {feeN}%<br />
              PV preview: <b>{previewPv.toFixed(2)}</b> = {pvN}% × ₱{commission.toFixed(2)}
            </div>
          </TabsContent>

          {hasVariants && (
            <TabsContent value="variations" className={tabBody}>
              <p className="mb-2 text-xs text-muted-foreground">
                Fee % / PV % cells show the inherited product-level rate — edit a cell to override
                just that variant.
              </p>
              <div className="overflow-x-auto">
                <VariantMatrix
                  product={product}
                  feeN={feeN}
                  pvN={pvN}
                  vDraft={vDraft}
                  setV={setV}
                />
              </div>
            </TabsContent>
          )}

          {rtuEnabled && (
            <TabsContent value="rtu" className={`${tabBody} space-y-4`}>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={rtuOn}
                  onChange={(e) => setRtuOn(e.target.checked)}
                />
                Enable Realtime Unilevel for this product
              </label>
              {rtuOn && (
                <>
                  <label className="block max-w-[10rem] space-y-1 text-sm">
                    <span className="font-medium">Pool ceiling %</span>
                    <Input value={ceiling} onChange={(e) => setCeiling(e.target.value)} />
                    <span className="block text-[10px] text-muted-foreground">% of commission</span>
                  </label>
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-muted-foreground">
                      <tr>
                        <th className="p-2">Level</th>
                        <th className="p-2">Share of pool %</th>
                        <th className="p-2">≈ Cash / unit</th>
                        <th className="p-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {shares.map((sh, idx) => {
                        const peso = poolPreview * ((Number(sh) || 0) / 100);
                        return (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="p-2 font-mono text-xs">
                              {idx === 0 ? 'L0 — buyer' : `L${idx}`}
                            </td>
                            <td className="p-2">
                              <Input
                                className="h-8 w-24"
                                value={sh}
                                onChange={(e) => setShareAt(idx, e.target.value)}
                              />
                            </td>
                            <td className="p-2 text-muted-foreground">₱{peso.toFixed(2)}</td>
                            <td className="p-2">
                              {shares.length > 1 && (
                                <button
                                  type="button"
                                  className="text-xs text-muted-foreground hover:text-destructive"
                                  onClick={() => removeLevel(idx)}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between">
                    <Button size="sm" variant="outline" onClick={addLevel}>
                      Add level
                    </Button>
                    <Button size="sm" variant="ghost" onClick={resetShares}>
                      Reset to defaults
                    </Button>
                  </div>
                  <p className="rounded-md border bg-muted/30 p-3 text-[11px] text-muted-foreground">
                    Pool ≈ <b>₱{poolPreview.toFixed(2)}</b> ({ceiling || 0}% of ₱
                    {commission.toFixed(2)} commission). Each level takes its own share of the pool.
                    {!features.merchant_sales_earn_comp && (
                      <span className="mt-1 block text-amber-600">
                        Members only earn this when “Merchant sales earn MLM commissions” is ON in
                        Settings.
                      </span>
                    )}
                  </p>
                </>
              )}
            </TabsContent>
          )}
        </Tabs>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={reject}>
            Reject
          </Button>
          <Button disabled={busy} onClick={approve}>
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Per-variant approval matrix: read-only price/SRP/stock/active + editable
 *  fee/PV/VAT overrides and a live PV preview per row. */
function VariantMatrix({
  product,
  feeN,
  pvN,
  vDraft,
  setV,
}: {
  product: AdminMerchantProductRow;
  feeN: number;
  pvN: number;
  vDraft: Record<string, VariantOverrideDraft>;
  setV: (id: string, patch: Partial<VariantOverrideDraft>) => void;
}) {
  return (
    <table className="w-full min-w-[640px] text-xs">
      <thead className="text-left text-muted-foreground">
        <tr>
          <th className="py-1 pr-2 font-medium">Variation</th>
          <th className="py-1 pr-2 font-medium">Price</th>
          <th className="py-1 pr-2 font-medium">Receivable</th>
          <th className="py-1 pr-2 font-medium">SRP</th>
          <th className="py-1 pr-2 font-medium">Stock</th>
          <th className="py-1 pr-2 font-medium">Active</th>
          <th className="py-1 pr-2 font-medium">Fee %</th>
          <th className="py-1 pr-2 font-medium">PV %</th>
          <th className="py-1 pr-2 font-medium">VAT</th>
          <th className="py-1 pr-2 font-medium text-right">PV preview</th>
        </tr>
      </thead>
      <tbody>
        {product.variants.map((v) => {
          const o = vDraft[v.id]!;
          const vPrice = Number(v.price) || 0;
          // Effective rates fall back to the product-level inputs when blank.
          const effFee = o.fee.trim() === '' ? feeN : Number(o.fee) || 0;
          const effPv = o.pv.trim() === '' ? pvN : Number(o.pv) || 0;
          const vPreview = ((vPrice * effFee) / 100) * (effPv / 100);
          return (
            <tr key={v.id} className="border-t">
              <td className="py-1 pr-2">
                <div className="font-medium text-foreground">{v.name}</div>
                <div className="text-[11px] text-muted-foreground">{v.sku}</div>
              </td>
              <td className="py-1 pr-2">₱{vPrice.toFixed(2)}</td>
              <td className="py-1 pr-2 tabular-nums text-emerald-600">
                ₱{merchantEarning(new Decimal(vPrice), effFee).toFixed(2)}
              </td>
              <td className="py-1 pr-2">{v.srp != null ? `₱${Number(v.srp).toFixed(2)}` : '—'}</td>
              <td className="py-1 pr-2">{v.stockQty ?? 0}</td>
              <td className="py-1 pr-2">
                {v.active ? (
                  <Badge variant="default" className="text-[10px]">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">
                    Off
                  </Badge>
                )}
              </td>
              <td className="py-1 pr-2">
                {/* Pre-filled with the inherited product-level rate (not just a
                    placeholder); clearing or matching it keeps the variant inheriting. */}
                <Input
                  className="h-7 w-14"
                  value={o.fee !== '' ? o.fee : String(feeN)}
                  onChange={(e) => setV(v.id, { fee: e.target.value })}
                />
              </td>
              <td className="py-1 pr-2">
                <Input
                  className="h-7 w-14"
                  value={o.pv !== '' ? o.pv : String(pvN)}
                  onChange={(e) => setV(v.id, { pv: e.target.value })}
                />
              </td>
              <td className="py-1 pr-2">
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={o.vatOn}
                    onChange={(e) => setV(v.id, { vatOn: e.target.checked })}
                  />
                  <Input
                    className="h-7 w-12"
                    value={o.vatPct}
                    disabled={!o.vatOn}
                    onChange={(e) => setV(v.id, { vatPct: e.target.value })}
                  />
                </label>
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">{vPreview.toFixed(2)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Payouts ─────────────────────────────────────────────────────────────
function PayoutsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('requested');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { data } = useQuery({
    queryKey: ['admin-merchant-payouts', status],
    queryFn: () => listMerchantPayouts({ status }),
  });
  const { data: batches } = useQuery({
    queryKey: ['admin-merchant-payout-batches'],
    queryFn: () => listMerchantPayoutBatches(),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-merchant-payouts'] });
    qc.invalidateQueries({ queryKey: ['admin-merchant-payout-batches'] });
  };

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const createBatch = async () => {
    if (selected.size === 0) return;
    // Default the batch period to the current month — admin can refine later.
    const now = new Date();
    const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
    await createMerchantPayoutBatch({ periodStart, periodEnd, requestIds: [...selected] });
    setSelected(new Set());
    invalidate();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <StatusFilter
          value={status}
          onChange={setStatus}
          options={['requested', 'processing', 'paid', 'rejected', 'cancelled']}
        />
        {status === 'requested' && (
          <Button size="sm" disabled={selected.size === 0} onClick={createBatch}>
            Batch {selected.size || ''} request{selected.size === 1 ? '' : 's'}
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3 w-8" />
                <th className="p-3">Merchant</th>
                <th className="p-3">Gross</th>
                <th className="p-3">Net</th>
                <th className="p-3">Method</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="p-3">
                    {r.status === 'requested' && (
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                    )}
                  </td>
                  <td className="p-3">
                    {r.businessName}
                    <div className="text-xs text-muted-foreground">{r.ownerEmail}</div>
                  </td>
                  <td className="p-3">₱{Number(r.amount).toFixed(2)}</td>
                  <td className="p-3">₱{Number(r.netAmount).toFixed(2)}</td>
                  <td className="p-3">
                    {r.method}
                    <div className="text-xs text-muted-foreground">
                      {String((r.payload as { account?: string })?.account ?? '')}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[r.status]} className="capitalize">
                      {r.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {r.status === 'requested' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => markMerchantPayoutPaid(r.id).then(invalidate)}
                        >
                          Mark paid
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectMerchantPayout(r.id).then(invalidate)}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {data && data.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    None.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Batches */}
      <h2 className="pt-2 text-lg font-semibold">Batches</h2>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th className="p-3">Period</th>
                <th className="p-3">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {(batches ?? []).map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="p-3 text-xs">
                    {b.periodStart.slice(0, 10)} → {b.periodEnd.slice(0, 10)}
                  </td>
                  <td className="p-3">
                    ₱{Number(b.total).toFixed(2)} {b.currency}
                  </td>
                  <td className="p-3">
                    <Badge variant={statusVariant[b.status]} className="capitalize">
                      {b.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => exportMerchantPayoutBatch(b.id)}
                    >
                      Export CSV
                    </Button>{' '}
                    {(b.status === 'processing' || b.status === 'queued') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => markMerchantPayoutBatchPaid(b.id).then(invalidate)}
                        >
                          Mark paid
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            rejectMerchantPayoutBatch(b.id, 'rejected by admin').then(invalidate)
                          }
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {batches && batches.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    No batches yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusFilter({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <Button
          key={o}
          size="sm"
          variant={value === o ? 'default' : 'outline'}
          className="capitalize"
          onClick={() => onChange(o)}
        >
          {o}
        </Button>
      ))}
    </div>
  );
}
